// AI 대화 상태 Context — 프롬프트 입력값·대화 턴·포커스 페이즈를 홈 화면과 공유
import React, { createContext, useContext, useState, useRef, ReactNode } from 'react';
import type { ConversationTurn, TriggerSource } from '../types';

export type ChatState = 'default' | 'focused' | 'responding';

interface ChatContextValue {
  chatState: ChatState;
  query: string;
  setQuery: (q: string) => void;
  turns: ConversationTurn[];
  addTurn: (trigger: TriggerSource) => void;
  resetTurns: () => void;
  enterFocused: () => void;
  exitFocused: () => void;
  sendMessage: (overrideQuery?: string) => void;
  cancelResponse: () => void;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
}

const ChatContext = createContext<ChatContextValue | null>(null);

export function ChatProvider({ children }: { children: ReactNode }) {
  const [chatState, setChatState] = useState<ChatState>('default');
  const [query, setQuery] = useState('');
  const queryRef = useRef('');
  const [turns, setTurns] = useState<ConversationTurn[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const generateId = () =>
    typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36);

  const addTurn = (trigger: TriggerSource) => {
    const newTurn: ConversationTurn = {
      id: generateId(),
      trigger,
    };
    setTurns(prev => [...prev, newTurn]);
    setChatState('responding');
  };

  const resetTurns = () => {
    setTurns([]);
  };

  const enterFocused = () => {
    setChatState('focused');
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const exitFocused = () => {
    setChatState('default');
  };

  const setQueryAndRef = (q: string) => {
    queryRef.current = q;
    setQuery(q);
  };

  const sendMessage = (overrideQuery?: string) => {
    const q = (overrideQuery ?? queryRef.current).trim();
    if (!q) return;
    addTurn({ type: 'user_input', label: q });
    setQueryAndRef('');
  };

  const cancelResponse = () => {
    setChatState('default');
    resetTurns();
  };

  return (
    <ChatContext.Provider value={{
      chatState, query, setQuery: setQueryAndRef,
      turns, addTurn, resetTurns,
      enterFocused, exitFocused, sendMessage, cancelResponse,
      textareaRef,
    }}>
      {children}
    </ChatContext.Provider>
  );
}

export function useChatContext() {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChatContext must be used inside ChatProvider');
  return ctx;
}
